/**
 * AdminAcquisition — first-party acquisition + activation dashboard.
 *
 * Why it exists:
 *   Google Analytics tells you that X visitors came from Facebook this
 *   month. It does NOT tell you which of those visitors signed up, used
 *   the product, came back, or paid. This page closes that gap by
 *   aggregating the operator's own data (`users` + `user_events` +
 *   `payment_transactions`) into the metrics that matter:
 *
 *     1. Weekly signup volume (last 12 weeks) with WoW delta
 *     2. Activation funnel (signed_in → created_resume → viewed_pricing
 *        → started_checkout → purchased), distinct-user counts, 30d
 *     3. Cohort retention (% of each signup-week that came back >=24h
 *        after signup) — answers "are my signups sticking?"
 *     4. Recent signups list with event count per user — surface the
 *        live new-user firehose for follow-up
 *     5. Top event types (30d) — useful for spotting hot funnel events
 *     6. Honest attribution-gap callout: signup_source isn't currently
 *        captured, so the page tells the operator what's missing.
 *
 * Read-only, admin-only, single API call (`/admin/acquisition`).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import {
  Activity, AlertTriangle, ArrowDown, ArrowUp, BarChart3, Loader2, Mail,
  UserPlus, Users, TrendingUp, Eye, ShoppingCart, FileText, MousePointerClick,
  Sparkles,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../utils/apiBase';
import useHideCookieBanner from '../utils/useHideCookieBanner';
import AdminSubNav from '../components/AdminSubNav';


// Tiny pure helper — relative-time formatter. Reused style across admin pages.
const fmtRelative = (iso) => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const diff = Date.now() - t;
  const s = Math.floor(diff / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  if (s < 86400 * 365) return `${Math.floor(s / 86400 / 30)}mo ago`;
  return `${Math.floor(s / 86400 / 365)}y ago`;
};


const StatCard = ({ icon: Icon, label, value, sublabel, testId }) => (
  <div className="bg-white border border-[#e5e7eb] p-4" data-testid={testId}>
    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#6b7280] mb-1">
      <Icon size={13} /> {label}
    </div>
    <div className="font-satoshi text-2xl font-black text-[#0f1115]">{value}</div>
    {sublabel && <div className="text-xs text-[#6b7280] mt-0.5">{sublabel}</div>}
  </div>
);


// Inline bar chart — pure SVG, no chart library dependency. Bars
// auto-scale to the max value so a slow week still shows shape.
const WeeklyBarChart = ({ data }) => {
  const max = Math.max(1, ...data.map((d) => d.n));
  const barW = 100 / Math.max(1, data.length);
  return (
    <svg viewBox="0 0 100 40" className="w-full h-32" data-testid="signups-chart" preserveAspectRatio="none">
      {data.map((d, i) => {
        const h = (d.n / max) * 36;
        const isLast = i === data.length - 1;
        return (
          <g key={d.week_start}>
            <title>{`Week of ${d.week_start}: ${d.n} signups`}</title>
            <rect
              x={i * barW + 0.5}
              y={40 - h - 2}
              width={barW - 1}
              height={h}
              fill={isLast ? '#002fa7' : '#a5b4fc'}
            />
          </g>
        );
      })}
    </svg>
  );
};


// Funnel row — each step renders its absolute count + the conversion %
// against the *previous* step, so the operator immediately sees where
// the leakiest dropoff is.
const FunnelStep = ({ icon: Icon, label, count, prevCount, testId }) => {
  const pct = prevCount > 0 ? Math.round((count / prevCount) * 100) : null;
  return (
    <div className="flex items-center gap-3 bg-white border border-[#e5e7eb] px-4 py-3" data-testid={testId}>
      <Icon size={16} className="text-[#002fa7] flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm text-[#0f1115]">{label}</div>
        {pct !== null && (
          <div className={`text-[11px] ${pct < 30 ? 'text-[#dc2626] font-bold' : 'text-[#6b7280]'}`}>
            {pct}% of previous step
          </div>
        )}
      </div>
      <div className="text-2xl font-black font-satoshi text-[#0f1115]">{count}</div>
    </div>
  );
};


export default function AdminAcquisition() {
  useHideCookieBanner();
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  // Test-account filter — default OFF (real-user signal only). Honest
  // numbers for client demos; the operator can flip it on to debug.
  const [includeTest, setIncludeTest] = useState(false);

  useEffect(() => {
    if (authLoading || !user) return undefined;
    if (user.role !== 'admin') return undefined;
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const r = await axios.get(
          `${API}/admin/acquisition?include_test=${includeTest}`,
          { withCredentials: true }
        );
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) setErr(e.response?.data?.detail || 'Failed to load acquisition data');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [user, authLoading, includeTest]);

  if (authLoading) return null;
  if (!user || user.role !== 'admin') return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="admin-acquisition">
      <AdminSubNav />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-satoshi text-3xl font-black text-[#0f1115]">Acquisition</h1>
            <p className="text-sm text-[#6b7280] mt-1">
              First-party signup volume, activation funnel, and cohort retention.
              All numbers derived from your own events — no Google Analytics dependency.
            </p>
          </div>
          {/* Test-account filter toggle — explicit because the default
              (exclude test accounts) silently strips data, and an operator
              looking at the raw numbers needs to know what's hidden. */}
          <div className="flex items-center gap-2 bg-white border border-[#e5e7eb] px-3 py-2" data-testid="test-filter-toggle">
            <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#6b7280]">Data</span>
            <button
              onClick={() => setIncludeTest(false)}
              className={`px-3 py-1 text-xs font-bold transition-colors ${!includeTest ? 'bg-[#0f1115] text-white' : 'text-[#4b5563] hover:bg-[#f4f5f7]'}`}
              data-testid="filter-real-only"
            >
              Real users only
            </button>
            <button
              onClick={() => setIncludeTest(true)}
              className={`px-3 py-1 text-xs font-bold transition-colors ${includeTest ? 'bg-[#0f1115] text-white' : 'text-[#4b5563] hover:bg-[#f4f5f7]'}`}
              data-testid="filter-include-test"
            >
              Include test accounts
            </button>
            {data?.filter?.excluded_test_users > 0 && !includeTest && (
              <span className="ml-2 text-[10px] text-[#6b7280] font-mono" data-testid="excluded-count">
                ({data.filter.excluded_test_users} hidden)
              </span>
            )}
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20 text-[#6b7280]" data-testid="loading">
            <Loader2 size={20} className="animate-spin mr-2" /> Loading acquisition data…
          </div>
        )}

        {err && !loading && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-800 flex items-center gap-2" data-testid="error">
            <AlertTriangle size={16} /> {err}
          </div>
        )}

        {data && !loading && (
          <>
            {/* Top-row stat cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="top-stats">
              <StatCard
                icon={Users}
                label="Total users"
                value={data.total_users.toLocaleString()}
                sublabel="all-time"
                testId="stat-total-users"
              />
              <StatCard
                icon={UserPlus}
                label="Signups this week"
                value={data.this_week}
                sublabel={
                  data.delta_pct === null
                    ? 'no prior week data'
                    : data.delta_pct > 0
                      ? `${data.delta_pct}% vs last week`
                      : `${data.delta_pct}% vs last week`
                }
                testId="stat-signups-week"
              />
              <StatCard
                icon={Activity}
                label="Activation rate 30d"
                value={data.activation_30d ? `${data.activation_30d.rate_pct}%` : '—'}
                sublabel={
                  data.activation_30d
                    ? `${data.activation_30d.activated} / ${data.activation_30d.new_users} new users`
                    : 'no data'
                }
                testId="stat-activation-rate"
              />
              <StatCard
                icon={ShoppingCart}
                label="Purchases 30d"
                value={data.funnel_30d.purchased || 0}
                sublabel="paid transactions"
                testId="stat-purchases-30d"
              />
            </div>

            {/* Weekly signups bar chart */}
            <div className="bg-white border border-[#e5e7eb] p-5 mb-6" data-testid="weekly-signups-card">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-satoshi text-lg font-bold text-[#0f1115] flex items-center gap-2">
                    <BarChart3 size={16} className="text-[#002fa7]" /> Weekly signups (last 12 weeks)
                  </h2>
                  <p className="text-xs text-[#6b7280] mt-0.5">
                    {data.this_week > data.last_week ? (
                      <span className="text-[#10b981] font-semibold inline-flex items-center gap-1">
                        <ArrowUp size={11} /> Trending up
                      </span>
                    ) : data.this_week < data.last_week ? (
                      <span className="text-[#dc2626] font-semibold inline-flex items-center gap-1">
                        <ArrowDown size={11} /> Trending down
                      </span>
                    ) : (
                      <span className="text-[#6b7280] font-semibold">Flat vs last week</span>
                    )}
                  </p>
                </div>
              </div>
              <WeeklyBarChart data={data.weekly_signups} />
              <div className="flex justify-between text-[10px] text-[#9ca3af] mt-1 font-mono">
                <span>{data.weekly_signups[0]?.week_start || ''}</span>
                <span>{data.weekly_signups[data.weekly_signups.length - 1]?.week_start || 'this week'}</span>
              </div>
            </div>

            {/* Activation funnel (30d) */}
            <div className="bg-white border border-[#e5e7eb] p-5 mb-6" data-testid="funnel-card">
              <h2 className="font-satoshi text-lg font-bold text-[#0f1115] flex items-center gap-2 mb-1">
                <TrendingUp size={16} className="text-[#002fa7] " /> Activation funnel (30d)
              </h2>
              <p className="text-xs text-[#6b7280] mb-4">
                Distinct users hitting each step. Red conversion % flags steps losing &gt; 70% of the previous step.
              </p>
              <div className="space-y-2">
                <FunnelStep icon={Activity}             label="Signed in"          count={data.funnel_30d.signed_in || 0}        prevCount={0}                                          testId="funnel-signed-in" />
                <FunnelStep icon={FileText}             label="Created resume"     count={data.funnel_30d.created_resume || 0}   prevCount={data.funnel_30d.signed_in || 0}             testId="funnel-created-resume" />
                <FunnelStep icon={Sparkles}             label="Activated"          count={data.funnel_30d.activated || 0}        prevCount={data.funnel_30d.signed_in || 0}             testId="funnel-activated" />
                <FunnelStep icon={Eye}                  label="Viewed pricing"     count={data.funnel_30d.viewed_pricing || 0}   prevCount={data.funnel_30d.activated || 0}             testId="funnel-viewed-pricing" />
                <FunnelStep icon={MousePointerClick}    label="Started checkout"   count={data.funnel_30d.started_checkout || 0} prevCount={data.funnel_30d.viewed_pricing || 0}        testId="funnel-started-checkout" />
                <FunnelStep icon={ShoppingCart}         label="Purchased"          count={data.funnel_30d.purchased || 0}        prevCount={data.funnel_30d.started_checkout || 0}      testId="funnel-purchased" />
              </div>
              {/* Phase-2 bridge — anonymous Free-ATS → signup attribution */}
              {typeof data.signup_from_free_ats_30d === 'number' && (
                <div
                  className="mt-4 pt-4 border-t border-[#e5e7eb] flex items-center justify-between text-sm"
                  data-testid="bridge-signup-from-free-ats"
                >
                  <span className="text-[#6b7280]">
                    <strong className="text-[#0f1115]">{data.signup_from_free_ats_30d}</strong> of these signups came from
                    <code className="text-[11px] bg-[#f4f5f7] px-1 py-0.5 ml-1 rounded">/free-ats-check</code>
                    <span className="text-[#9ca3af]"> (visitor → user bridge)</span>
                  </span>
                </div>
              )}
            </div>

            {/* Cohort retention */}
            <div className="bg-white border border-[#e5e7eb] p-5 mb-6" data-testid="cohort-card">
              <h2 className="font-satoshi text-lg font-bold text-[#0f1115] flex items-center gap-2 mb-1">
                <Users size={16} className="text-[#002fa7]" /> Cohort retention (last 6 weeks)
              </h2>
              <p className="text-xs text-[#6b7280] mb-4">
                % of each signup-week cohort that came back &gt;= 24h after signup. Bigger is better.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#f4f5f7] text-[#6b7280] uppercase text-xs tracking-wider">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Cohort (week of)</th>
                      <th className="px-3 py-2 text-right font-semibold">Signups</th>
                      <th className="px-3 py-2 text-right font-semibold">Returned 24h+</th>
                      <th className="px-3 py-2 text-right font-semibold">Retention %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#e5e7eb]">
                    {data.cohorts.map((c) => (
                      <tr key={c.week_start} data-testid={`cohort-row-${c.week_start}`}>
                        <td className="px-3 py-2 text-[#0f1115] font-mono text-xs">{c.week_start}</td>
                        <td className="px-3 py-2 text-right font-mono">{c.cohort_size}</td>
                        <td className="px-3 py-2 text-right font-mono">{c.returned_after_24h}</td>
                        <td className={`px-3 py-2 text-right font-bold ${
                          c.retention_pct >= 50 ? 'text-[#10b981]' :
                          c.retention_pct >= 25 ? 'text-[#f59e0b]' : 'text-[#dc2626]'
                        }`}>
                          {c.cohort_size === 0 ? '—' : `${c.retention_pct}%`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Recent signups */}
            <div className="bg-white border border-[#e5e7eb] p-5 mb-6" data-testid="recent-signups-card">
              <h2 className="font-satoshi text-lg font-bold text-[#0f1115] flex items-center gap-2 mb-1">
                <UserPlus size={16} className="text-[#002fa7]" /> Recent signups (20 most recent)
              </h2>
              <p className="text-xs text-[#6b7280] mb-4">
                Live new-user firehose — the warmest leads for direct follow-up.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#f4f5f7] text-[#6b7280] uppercase text-xs tracking-wider">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Email</th>
                      <th className="px-3 py-2 text-left font-semibold">Tier</th>
                      <th className="px-3 py-2 text-left font-semibold">Signed up</th>
                      <th className="px-3 py-2 text-left font-semibold">Last active</th>
                      <th className="px-3 py-2 text-right font-semibold">Events</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#e5e7eb]">
                    {data.recent_signups.map((u) => (
                      <tr key={u.user_id} data-testid={`recent-row-${u.user_id}`} className="hover:bg-[#f9fafb]">
                        <td className="px-3 py-2 text-[#0f1115] font-mono text-xs">{u.email}</td>
                        <td className="px-3 py-2 text-[#0f1115] text-xs uppercase">{u.subscription_tier || 'free'}</td>
                        <td className="px-3 py-2 text-[#4b5563]">{fmtRelative(u.created_at)}</td>
                        <td className="px-3 py-2">
                          {u.last_active_at
                            ? <span className="text-[#4b5563]">{fmtRelative(u.last_active_at)}</span>
                            : <span className="text-[#dc2626] font-semibold">never</span>}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-[#0f1115]">{u.event_count ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Top events */}
            <div className="bg-white border border-[#e5e7eb] p-5 mb-6" data-testid="top-events-card">
              <h2 className="font-satoshi text-lg font-bold text-[#0f1115] flex items-center gap-2 mb-1">
                <Activity size={16} className="text-[#002fa7]" /> Top events (30d)
              </h2>
              <p className="text-xs text-[#6b7280] mb-4">
                Volume by event_type — useful for spotting which funnel moments are firing.
              </p>
              <div className="space-y-1.5">
                {data.top_events_30d.map((e) => {
                  const max = Math.max(1, ...data.top_events_30d.map((x) => x.n));
                  const pctW = (e.n / max) * 100;
                  return (
                    <div key={e.event_type} className="flex items-center gap-3" data-testid={`event-row-${e.event_type}`}>
                      <div className="w-48 truncate text-xs font-mono text-[#4b5563]">{e.event_type}</div>
                      <div className="flex-1 bg-[#f4f5f7] h-4 relative">
                        <div className="absolute inset-y-0 left-0 bg-[#002fa7]" style={{ width: `${pctW}%` }} />
                      </div>
                      <div className="w-12 text-right font-mono text-xs text-[#0f1115]">{e.n}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Attribution gap callout — honest about what's NOT captured */}
            <div className="bg-[#fffbeb] border-l-4 border-[#f59e0b] p-4 flex items-start gap-3" data-testid="attribution-gap">
              <AlertTriangle size={18} className="text-[#f59e0b] flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-bold text-[#92400e]">Traffic-source attribution gap</div>
                <div className="text-[#92400e] mt-1 leading-relaxed">
                  Only <strong>{data.attribution_gap.users_with_signup_source}</strong> users have <code>signup_source</code> captured.
                  To see which traffic sources convert to paying users, instrument <code>signup_source</code>, <code>signup_utm_source</code>,
                  and <code>referrer</code> capture on <code>/register</code> (read from URL query + document.referrer).
                  Until then, Google Analytics remains the only source-attribution view.
                </div>
              </div>
            </div>

            <div className="mt-4 text-[10px] text-[#9ca3af] text-right font-mono">
              as of {new Date(data.as_of).toLocaleString()}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
